import {DateTime} from "luxon";
import {ItemType, ShipType} from "../../../types/swc.types.ts";
import {
    ProductionGroup,
    ProductionPlannerBlock,
    ProductionPlannerProductType,
    ProductionPlannerRun,
    ProductionTrackedEntity
} from "./types.ts";
import {
    LocationSelectorGroup,
    LocationSelectorOption,
    LocationWarningLevel,
    PlannerPlanStatusSummary,
    PlannerProduct,
    PlannerStationCatalog
} from "./plannerTypes.ts";

const TYPE_STATION_FACTORY = "5:5";
const TYPE_FACILITY_FACTORY = "4:34";
const TYPE_FACILITY_NAVAL_SHIPYARD = "4:73";
const STATION_SHIPYARD_TYPES = new Set(["5:26", "5:6", "5:7", "5:8"]);
const UNGROUPED_LOCATION_GROUP_ID = "__ungrouped__";

export function getRunSortOrder(run: ProductionPlannerRun): number {
    return typeof run.sortOrder === "number" && Number.isFinite(run.sortOrder)
        ? run.sortOrder
        : Number.MAX_SAFE_INTEGER;
}

export function getLocationWarningLevel(entity: ProductionTrackedEntity): LocationWarningLevel {
    if (entity.currentAction) {
        return "active";
    }
    if ((entity.queuedItems ?? []).length > 0) {
        return "queued";
    }
    return null;
}

export function hasDatacardForProduct(entity: {entityData?: Record<string, unknown>}, productId: string): boolean {
    if (!productId) {
        return false;
    }
    const data = entity.entityData && typeof entity.entityData === "object" ? entity.entityData : {};
    const cards = Array.isArray(data.assignedDatacards) ? data.assignedDatacards : [];
    return cards.some((card) => {
        if (!card || typeof card !== "object") {
            return false;
        }
        const type = (card as {type?: unknown}).type;
        if (!type || typeof type !== "object") {
            return false;
        }
        return (type as {id?: unknown}).id === productId;
    });
}

export function cloneRun(run: ProductionPlannerRun): ProductionPlannerRun {
    const cloned = JSON.parse(JSON.stringify(run)) as ProductionPlannerRun;
    cloned.managementSkill = normalizeManagementSkill((cloned as {managementSkill?: unknown}).managementSkill);
    return cloned;
}

export function newBlock(): ProductionPlannerBlock {
    return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        productType: "ship",
        productId: "",
        quantityPerLocation: 1,
        repeat: 1,
        locationEntityIds: [],
    };
}

export function parseIntInput(value: string | number, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return fallback;
    }
    return Math.max(0, Math.floor(parsed));
}

export function normalizeManagementSkill(value: unknown): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return 0;
    }
    return Math.max(0, Math.min(5, parsed));
}

export function getEntityTypeData(entity: ProductionTrackedEntity): Record<string, unknown> {
    const row = entity.entityData;
    if (!row || typeof row !== "object") {
        return {};
    }
    return row as Record<string, unknown>;
}

export function getEntityTypeId(entity: ProductionTrackedEntity): string {
    const data = getEntityTypeData(entity);
    const value = data.typeId;
    if (typeof value === "string") {
        return value.trim();
    }
    return "";
}

export function getEntityTypeName(entity: ProductionTrackedEntity): string {
    const data = getEntityTypeData(entity);
    const value = data.typeName;
    if (typeof value === "string" && value.trim() !== "") {
        return value.trim();
    }
    return entity.entityType === "facility" ? "Facility" : "Station";
}

export function formatEta(ts: number | null): string {
    if (!ts) {
        return "-";
    }
    return DateTime.fromSeconds(ts).toFormat("LLL dd HH:mm");
}

export function formatDateTime(ts: number | null): string {
    if (!ts) {
        return "-";
    }
    return DateTime.fromSeconds(ts).toFormat("LLL dd yyyy HH:mm");
}

export function formatNumber(value: number): string {
    return new Intl.NumberFormat().format(value);
}

export function isWeaponLikeItem(className?: string): boolean {
    return (className ?? "").toLowerCase().includes("weapon");
}

export function buildIdMap<T extends {id: string}>(rows: T[]): Map<string, T> {
    const map = new Map<string, T>();
    rows.forEach((row) => map.set(row.id, row));
    return map;
}

export function getLocationSupportError(
    location: ProductionTrackedEntity,
    productType: ProductionPlannerProductType,
    product: PlannerProduct | null,
    stationTypeById: PlannerStationCatalog
): string | null {
    if (!product) {
        return "Select a product first";
    }

    const locationTypeId = getEntityTypeId(location);
    const isFactory = locationTypeId === TYPE_STATION_FACTORY || locationTypeId === TYPE_FACILITY_FACTORY;
    const isX7Station = locationTypeId === TYPE_STATION_FACTORY;
    const isStationShipyard = STATION_SHIPYARD_TYPES.has(locationTypeId);
    const isNavalShipyard = locationTypeId === TYPE_FACILITY_NAVAL_SHIPYARD;

    if (productType !== "ship") {
        if (!isFactory) {
            return `${productType} products can only be produced in factories/X7 stations`;
        }
        return null;
    }

    const ship = product as ShipType;
    const className = (ship.class ?? "").toLowerCase();
    const isBoatOrSubmarine = className.includes("boats and submarines");
    if (isBoatOrSubmarine) {
        if (!isNavalShipyard) {
            return "Boats and submarines can only be produced in naval shipyards";
        }
        return null;
    }

    const productLength = Number(ship.length ?? 0);
    if (isFactory || isX7Station) {
        if (!(productLength > 0 && productLength < 80)) {
            return "Factories/X7 can only build ships strictly shorter than 80m";
        }
        return null;
    }

    if (isStationShipyard) {
        const stationType = stationTypeById.get(locationTypeId);
        const stationLength = Number(stationType?.length ?? 0);
        if (!(stationLength > 0 && productLength > 0)) {
            return "Missing length data for shipyard restriction";
        }
        if (productLength > stationLength && stationLength < 3000) {
            return `Ship length ${productLength}m exceeds shipyard max ${stationLength}m`;
        }
        return null;
    }

    return "Selected location does not support this ship";
}

export function formatRelativeDuration(seconds: number): string {
    const clamped = Math.max(0, Math.floor(seconds));
    const days = Math.floor(clamped / 86400);
    const hours = Math.floor((clamped % 86400) / 3600);
    const minutes = Math.floor((clamped % 3600) / 60);
    if (days > 0) {
        return `${days}d ${hours}h`;
    }
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
}

export function formatDurationRange(minAt: number | null, maxAt: number | null, nowSeconds: number): string {
    if (!minAt || !maxAt) {
        return "Unknown";
    }
    const minText = formatRelativeDuration(minAt - nowSeconds);
    const maxText = formatRelativeDuration(maxAt - nowSeconds);
    if (minText === maxText) {
        return minText;
    }
    return `${minText} - ${maxText}`;
}

export function formatEtaWindow(minAt: number | null, maxAt: number | null): string {
    if (!minAt || !maxAt) {
        return "Unknown";
    }
    const minText = formatEta(minAt);
    const maxText = formatEta(maxAt);
    if (minText === maxText) {
        return minText;
    }
    return `${minText} - ${maxText}`;
}

export function getLocationStatusDisplay(entity: ProductionTrackedEntity): {label: string; color: string} {
    if (entity.currentAction) {
        const etaText = formatEta(entity.currentAction.endAt ?? null);
        return {
            label: etaText !== "-" ? `Producing (${etaText})` : "Producing",
            color: "red",
        };
    }
    if ((entity.queuedItems ?? []).length > 0) {
        return {label: "Queued", color: "yellow"};
    }
    return {label: "Idle", color: "green"};
}

export function buildEmptyPlanStatusSummary(): PlannerPlanStatusSummary {
    return {
        total: 0,
        active: 0,
        queued: 0,
        completed: 0,
        pending: 0,
    };
}

export function getPlanProgressStatus(summary: PlannerPlanStatusSummary): {label: string; color: string} {
    if (summary.total > 0 && summary.completed >= summary.total) {
        return {label: "Completed", color: "green"};
    }
    // Any completed or active unit means production has already started.
    if (summary.active > 0 || summary.completed > 0) {
        return {label: "In progress", color: "yellow"};
    }
    if (summary.pending > 0 || summary.total === 0) {
        return {label: "Pending", color: "gray"};
    }
    return {label: "Queued", color: "blue"};
}

export function getBlockKey(block: ProductionPlannerBlock, index: number): string {
    const id = (block.id ?? "").trim();
    return id !== "" ? id : `index:${index}`;
}

export function splitBlockLocationIds(locationIds: string[]): {facilities: string[]; stations: string[]} {
    return locationIds.reduce<{facilities: string[]; stations: string[]}>((acc, locationId) => {
        const normalizedId = locationId.trim();
        if (normalizedId.startsWith("4:")) {
            acc.facilities.push(normalizedId.slice(2));
        } else if (normalizedId.startsWith("5:")) {
            acc.stations.push(normalizedId.slice(2));
        }
        return acc;
    }, {facilities: [], stations: []});
}

export function buildLocationSelectorGroups(
    compatibleLocations: ProductionTrackedEntity[],
    optionById: Map<string, LocationSelectorOption>,
    moduleGroups: ProductionGroup[]
): LocationSelectorGroup[] {
    const groupMap = new Map<string, {id: string; name: string; entityIds: Set<string>}>();
    const groupedEntityIds = new Set<string>();

    const ensureGroup = (id: string, name: string) => {
        if (!groupMap.has(id)) {
            groupMap.set(id, {id, name, entityIds: new Set<string>()});
        }
        return groupMap.get(id)!;
    };

    moduleGroups.forEach((group) => {
        const groupName = (group.name || "").trim() || "Unnamed Group";
        const groupId = (group.id || "").trim() || `group:${groupName.toLowerCase()}`;
        const bucket = ensureGroup(groupId, groupName);
        (group.entityIds ?? []).forEach((entityId) => {
            if (optionById.has(entityId)) {
                bucket.entityIds.add(entityId);
                groupedEntityIds.add(entityId);
            }
        });
    });

    compatibleLocations.forEach((entity) => {
        const refs = entity.groups ?? [];
        let grouped = groupedEntityIds.has(entity.entityId);
        refs.forEach((groupRef) => {
            const groupName = (groupRef.name || "").trim();
            if (groupName === "") {
                return;
            }
            const groupId = (groupRef.id || "").trim() || `group:${groupName.toLowerCase()}`;
            ensureGroup(groupId, groupName).entityIds.add(entity.entityId);
            grouped = true;
        });
        if (!grouped) {
            ensureGroup(UNGROUPED_LOCATION_GROUP_ID, "Ungrouped").entityIds.add(entity.entityId);
        }
    });

    return Array.from(groupMap.values())
        .map((group) => ({
            id: group.id,
            name: group.name,
            entityIds: Array.from(group.entityIds).sort((a, b) => {
                const aName = optionById.get(a)?.name ?? a;
                const bName = optionById.get(b)?.name ?? b;
                return aName.localeCompare(bName);
            }),
        }))
        .filter((group) => group.entityIds.length > 0)
        .sort((a, b) => {
            if (a.id === UNGROUPED_LOCATION_GROUP_ID) {
                return 1;
            }
            if (b.id === UNGROUPED_LOCATION_GROUP_ID) {
                return -1;
            }
            return a.name.localeCompare(b.name);
        });
}

export function toSelectOptions(rows: Array<{id: string; name: string}>): Array<{value: string; label: string}> {
    return rows
        .toSorted((a, b) => a.name.localeCompare(b.name))
        .map((row) => ({value: row.id, label: row.name}));
}

export function filterPlannerItems(items: ItemType[]): ItemType[] {
    return items.filter((row) => !isWeaponLikeItem(row.class));
}
